import Image from 'next/future/image';
import { Tab } from '@headlessui/react';
import cn from 'classnames';
import { useEffect, useMemo, useState } from 'react';
import { debounce } from 'lodash';
import settings from '@/settings/index';

export type DeviceList = {
  image: string;
  text: string;
  link?: string;
};

export type DeviceListWithConditions = {
  new: Array<DeviceList>;
  used: Array<DeviceList>;
};

const SearchInput = ({
  value,
  onChange,
  clearInput,
}: {
  value: string;
  onChange: (value: string) => void;
  clearInput: () => void;
}) => (
  <div className="relative mb-6 inline-block w-full sm:w-auto">
    <input
      type="text"
      value={value}
      className="w-full rounded-lg border border-blue-500 px-3 py-3 pr-12 text-sm text-blue-500 outline-none placeholder:text-gray-400 xs:text-base"
      placeholder={
        settings.store === 'reebelo-us'
          ? 'Search device model'
          : 'Which device do you want to sell?'
      }
      onChange={(event) => onChange(event?.target.value)}
    />
    {value !== '' && (
      <div
        className="absolute inset-y-0 right-0 flex cursor-pointer items-center pr-3"
        onClick={clearInput}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 20 20"
          fill="currentColor"
          aria-hidden="true"
          className="h-5 w-5 text-blue-500"
        >
          <path
            fill-rule="evenodd"
            d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
            clip-rule="evenodd"
          ></path>
        </svg>
      </div>
    )}
  </div>
);

const ProductList = (props: { devices: DeviceList[] }) => (
  <>
    <ul className="flex flex-wrap bg-white">
      {props.devices &&
        props.devices.map(
          ({
            image,
            text,
            link,
          }: {
            image: string;
            text: string;
            link?: string;
          }) => (
            <li
              key={`${image}--${text}`}
              className="flex w-1/3 border border-transparent border-r-gray-500 border-b-gray-500 py-4 px-2.5 text-center md:w-1/6"
            >
              <a
                href={link ?? `https://sell.reebelo.com.au/`}
                className="flex w-full flex-wrap"
              >
                <div className="relative mx-auto mb-1 h-[70px] w-[70px] items-center justify-center xxs:h-[80px] xxs:w-[80px] ">
                  <Image
                    alt={text}
                    src={image}
                    width={80}
                    height={80}
                    className="h-full w-full object-contain"
                  />
                </div>
                <p className="mt-1 w-full text-xs font-semibold leading-tight xs:text-sm">
                  {text}
                </p>
              </a>
            </li>
          ),
        )}
    </ul>
    {props.devices?.length === 0 && <div>No product available.</div>}
  </>
);

const tabSelectedCss = (selected: boolean) => ({
  '!bg-blue-500 !text-white': selected,
});
const onTabCss = (onTab: boolean) => ({
  '!rounded-lg xs:!rounded-xl': onTab,
});

export default function ProductsListLayout({
  devices,
  customText,
}: {
  devices: DeviceList[] | DeviceListWithConditions;
  customText?: string;
}) {
  const commonH2Css =
    settings.store === 'quista'
      ? 'mb-2 text-xl font-bold text-blue-500 text-xl sm:text-2xl sm:mb-3 !leading-tight'
      : 'mb-3 text-xl font-bold text-gray-700 xs:mb-5 xs:text-2xl sm:text-3xl md:mb-8';

  const CommonHeading = () => (
    <h2 className={commonH2Css}>Select your device model</h2>
  );
  const commonTabCss = 'w-full outline-none xxs:w-1/2 sm:w-auto';
  const commonTabInnerCss =
    'bg-white border border-blue-500 px-4 sm:px-8 py-3 xs:py-4 font-bold text-blue-500 min-w-0 sm:min-w-[200px] outline-none text-base !leading-tight';

  const [filteredDevices, setFilteredDevices] = useState(devices);
  const [searchInput, setSearchInput] = useState('');

  // to hide New and Used tab selection if one of the list is empty
  const shouldShowTab = Array.isArray(devices)
    ? false
    : (devices as DeviceListWithConditions).new.length > 0 ||
      (devices as DeviceListWithConditions).used.length > 0;
  const oneTabPresetOnly =
    shouldShowTab &&
    ((devices as DeviceListWithConditions).new.length === 0 ||
      (devices as DeviceListWithConditions).used.length === 0);

  const lowerCaseMatch = (text: string, match: string) =>
    text.toLowerCase().includes(match.toLowerCase());

  const debounceSearch = useMemo(
    () =>
      debounce((val: string) => {
        if (val === '') setFilteredDevices(devices);

        if (val !== '' && shouldShowTab) {
          setFilteredDevices(() => ({
            new: (devices as DeviceListWithConditions).new.filter((d) =>
              lowerCaseMatch(d.text, val),
            ),
            used: (devices as DeviceListWithConditions).used.filter((d) =>
              lowerCaseMatch(d.text, val),
            ),
          }));
        }
      }, 500),
    [],
  );

  useEffect(() => {
    debounceSearch(searchInput);
  }, [searchInput]);

  const handleInputChange = (val: string) => {
    setSearchInput(val);
    debounceSearch(val);
  };

  return (
    <>
      {settings.store === 'quista' && (
        <>
          {shouldShowTab && (
            <div>
              <h2 className={commonH2Css}>What do you want to sell today ?</h2>
            </div>
          )}
          <Tab.Group>
            {shouldShowTab && (
              <Tab.List className="mb-6 flex flex-col flex-wrap xxs:flex-row xxs:flex-nowrap sm:mb-8">
                {(devices as DeviceListWithConditions).used.length > 0 && (
                  <Tab className={commonTabCss}>
                    {({ selected }) => (
                      <div
                        className={cn(
                          'rounded-l-none rounded-t-lg xxs:rounded-l-lg xxs:rounded-tr-none xs:rounded-l-xl',
                          commonTabInnerCss,
                          tabSelectedCss(selected),
                          onTabCss(oneTabPresetOnly),
                        )}
                      >
                        Used {customText}
                      </div>
                    )}
                  </Tab>
                )}
                {(devices as DeviceListWithConditions).new.length > 0 && (
                  <Tab className={commonTabCss}>
                    {({ selected }) => (
                      <div
                        className={cn(
                          'rounded-r-none rounded-b-lg xxs:rounded-r-lg xxs:rounded-bl-none xs:rounded-r-xl',
                          commonTabInnerCss,
                          tabSelectedCss(selected),
                          onTabCss(oneTabPresetOnly),
                        )}
                      >
                        New {customText}
                      </div>
                    )}
                  </Tab>
                )}
              </Tab.List>
            )}
            <Tab.Panels>
              <Tab.Panel>
                <SearchInput
                  value={searchInput}
                  clearInput={() => {
                    setSearchInput('');
                  }}
                  onChange={(val) => handleInputChange(val)}
                />
                <CommonHeading />
                <ProductList
                  devices={(filteredDevices as DeviceListWithConditions).used}
                />
              </Tab.Panel>
              <Tab.Panel>
                <SearchInput
                  clearInput={() => {
                    setSearchInput('');
                  }}
                  value={searchInput}
                  onChange={(val) => handleInputChange(val)}
                />
                <CommonHeading />
                <ProductList
                  devices={(filteredDevices as DeviceListWithConditions).new}
                />
              </Tab.Panel>
            </Tab.Panels>
          </Tab.Group>
        </>
      )}
      {settings.store === 'reebelo-us' && (
        <>
          <div>
            <h2 className={commonH2Css}>What do you want to sell today ?</h2>
          </div>
          <SearchInput
            value={searchInput}
            clearInput={() => {
              setSearchInput('');
            }}
            onChange={(val) => handleInputChange(val)}
          />
          <CommonHeading />
          <ProductList
            devices={(filteredDevices as DeviceListWithConditions).used}
          />
        </>
      )}
      {settings.store !== 'quista' && settings.store !== 'reebelo-us' && (
        <>
          <CommonHeading />
          <ProductList devices={devices as DeviceList[]} />
        </>
      )}
    </>
  );
}
